import humps from 'humps'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useNuxtApp } from '@nuxt/bridge/dist/runtime'
import gachaAnimationTypes from '@/static/gachaAnimationTypes.json'
import { useGachaMaster } from './useGachaMaster'

export const useGachaStore = defineStore('gachaStore', () => {
  const tapCountTreasureType = { 3: [1, 2, 3, 4, 5], 4: [6, 7, 8, 9], 5: [10] }

  // state
  const gachaTypes = ref([])
  const gachaHistories = ref([])
  const gachaResult = ref(null)
  const gachaRewards = ref([])
  const gachaTresureType = ref(0)

  // getters
  const getGachaType = computed(() => gachaType => gachaTypes.value.find(obj => obj.gachaType === gachaType))
  const getGachaTypes = computed(() => [...gachaTypes.value])
  const getCurrentGachaTypeMasters = computed(() => {
    const gachaTypeSeqMap = new Map(
      gachaTypes.value.map(obj => [obj.gachaType, obj.seq])
    );
  
    return useGachaMaster().getGachaTypeMasters
      .filter(obj => gachaTypeSeqMap.has(obj.gachaType))
      .map(obj => ({
        ...obj,
        seq: gachaTypeSeqMap.get(obj.gachaType),
      }));
  });
  const getGachaTypeHistories = computed(() => [...gachaHistories.value])
  const getGachaResult = computed(() => gachaResult.value)
  const getTresureType = computed(() => gachaTresureType.value)
  const getGachaReward = computed(() => gachaRewardId =>
    gachaRewards.value.find(obj => obj.gachaRewardId === gachaRewardId)
  )
  const getGachaAnimationType = computed(() => {
    let higestTreasureType = 0
    let highestCount = 0
    let tapCounts = []

    const mintedResults = gachaResult.value.filter(obj => obj.mintedId != 0)
    if (mintedResults.length > 0) {
      // アセットあり
      for (const r of mintedResults) {
        let gachaReward = getGachaReward.value(r.gachaRewardId)
        if (gachaReward.treasureType > higestTreasureType) {
          higestTreasureType = gachaReward.treasureType
        }

        let tapCount =
          tapCountTreasureType[gachaReward.treasureType][
            Math.floor(Math.random() * tapCountTreasureType[gachaReward.treasureType].length)
          ]
        tapCounts.push({ tapCount, mintedId: r.mintedId })
        if (highestCount < tapCount) {
          highestCount = tapCount
        }
      }
    } else {
      // アセットなし
      for (const r of gachaResult.value) {
        let gachaReward = getGachaReward.value(r.gachaRewardId)
        if (gachaReward.treasureType > higestTreasureType) {
          higestTreasureType = gachaReward.treasureType
        }
      }
    }

    // gachaAnimationTypesからhighestRarity持っているrarity[]のみを抽出
    const filteredGachaAnimationTypes = gachaAnimationTypes.filter(
      obj => obj.treasureType.includes(higestTreasureType) && (highestCount == 0 || obj.tapCount.includes(highestCount))
    )

    // それらのpowerの合計を計算
    const totalPower = filteredGachaAnimationTypes.reduce((acc, obj) => {
      return acc + obj.power
    }, 0)

    // 乱数を生成
    const random = Math.round(Math.random() * totalPower)

    // 乱数によって決定
    let sum = 0
    for (const gachaAnimationType of filteredGachaAnimationTypes) {
      sum += gachaAnimationType.power
      if (random < sum) {
        return { name: gachaAnimationType.name, tapCounts }
      }
    }

    // 見つからない場合は最後のものを返す
    return { name: gachaAnimationTypes[gachaAnimationTypes.length - 1].name, tapCounts }
  })

  // actions
  async function fetchGachaTypes() {
    const { $gachaService } = useNuxtApp()
    const res = await $gachaService.getCurrentGachaTypes()
    gachaTypes.value = res

    gachaRewards.value = []
    for (const gachaType of gachaTypes.value) {
      gachaRewards.value = [...gachaRewards.value, ...gachaType.gachaRewardsList]
    }
  }

  async function fetchUserGachaHistories(gachaType) {
    const { $gachaService } = useNuxtApp()
    const res = await $gachaService.getUserGachaHistories(gachaType)
    gachaHistories.value = humps.camelizeKeys(res)
  }

  async function gacha(gachaType, gachaPaymentType, isMulti) {
    const { $gachaService } = useNuxtApp()
    const res = await $gachaService.gacha(gachaType, gachaPaymentType, isMulti)
    gachaResult.value = res.userGachaResultsList
    gachaTresureType.value = res.treasureType
  }

  return {
    gachaTypes,
    gachaHistories,
    gachaResult,
    gachaRewards,
    gachaTresureType,
    getGachaType,
    getGachaTypes,
    getCurrentGachaTypeMasters,
    getGachaTypeHistories,
    getGachaResult,
    getTresureType,
    getGachaReward,
    getGachaAnimationType,
    fetchGachaTypes,
    fetchUserGachaHistories,
    gacha,
  }
})
